import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { t3, pick3 } from "../i18n/v3";

function Col({ heading, children, testId }) {
    return (
        <div data-testid={testId}>
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] mb-5">
                {heading}
            </div>
            <ul className="flex flex-col gap-3 text-[14px] text-[color:var(--navy)]">
                {children}
            </ul>
        </div>
    );
}

const FLink = ({ to, children }) => (
    <li>
        <Link to={to} className="link-editorial">{children}</Link>
    </li>
);

export default function FooterV3() {
    const { lang } = useLanguage();
    return (
        <footer data-testid="site-footer" className="mt-16 border-t border-[color:var(--line)] bg-[color:var(--ivory)]">
            <div className="max-w-[1200px] mx-auto px-6 md:px-10 pt-16 pb-10">
                <div className="grid grid-cols-12 gap-8">
                    <div className="col-span-12 md:col-span-4">
                        <img
                            src="/brand/logo.png"
                            alt={pick3(t3.brand, lang)}
                            className="w-[160px] h-auto mb-6"
                            draggable="false"
                        />
                        <p className="font-display italic text-[20px] leading-[1.35] text-[color:var(--navy)] max-w-[28ch]">
                            {pick3(t3.tagline, lang)}
                        </p>
                    </div>

                    <div className="col-span-6 md:col-span-2 md:col-start-6">
                        <Col heading={pick3(t3.footer.explore, lang)} testId="footer-explore">
                            <FLink to="/ideas">{pick3(t3.nav.ideas, lang)}</FLink>
                            <FLink to="/research">{pick3(t3.nav.research, lang)}</FLink>
                            <FLink to="/projects">{pick3(t3.nav.projects, lang)}</FLink>
                            <FLink to="/memoire">{pick3(t3.nav.memoire, lang)}</FLink>
                            <FLink to="/philosophy">{pick3(t3.nav.philosophy, lang)}</FLink>
                        </Col>
                    </div>

                    <div className="col-span-6 md:col-span-2">
                        <Col heading={pick3(t3.footer.aboutH, lang)} testId="footer-about">
                            <FLink to="/about">{pick3(t3.nav.about, lang)}</FLink>
                            <FLink to="/lighthouse">{pick3(t3.nav.lighthouse, lang)}</FLink>
                            <FLink to="/about#origin">{lang === "es" ? "Origen" : lang === "nl" ? "Oorsprong" : lang === "fr" ? "Origine" : "Origin"}</FLink>
                        </Col>
                    </div>

                    <div className="col-span-12 md:col-span-2">
                        <Col heading={pick3(t3.footer.connect, lang)} testId="footer-connect">
                            <FLink to="/contact">{pick3(t3.nav.contact, lang)}</FLink>
                        </Col>
                    </div>
                </div>

                <div className="mt-16 pt-6 border-t border-[color:var(--line)] flex flex-wrap items-center justify-between gap-4">
                    <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]">
                        {pick3(t3.footer.rights, lang)}
                    </p>
                    <nav aria-label="Legal" data-testid="footer-legal" className="flex flex-wrap gap-x-4 gap-y-2 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]">
                        <Link to="/legal-notice" className="link-editorial hover:text-[color:var(--navy)]">{
                            lang === "es" ? "Aviso legal" : lang === "nl" ? "Colofon" : lang === "fr" ? "Mentions légales" : "Legal notice"
                        }</Link>
                        <Link to="/privacy" className="link-editorial hover:text-[color:var(--navy)]">{
                            lang === "es" ? "Privacidad" : lang === "nl" ? "Privacy" : lang === "fr" ? "Confidentialité" : "Privacy"
                        }</Link>
                        <Link to="/cookies" className="link-editorial hover:text-[color:var(--navy)]">Cookies</Link>
                        <Link to="/disclaimer" className="link-editorial hover:text-[color:var(--navy)]">{
                            lang === "es" ? "Aviso" : lang === "nl" ? "Disclaimer" : lang === "fr" ? "Avertissement" : "Disclaimer"
                        }</Link>
                        <Link to="/copyright" className="link-editorial hover:text-[color:var(--navy)]">{
                            lang === "es" ? "Propiedad intelectual" : lang === "nl" ? "Auteursrecht" : lang === "fr" ? "Droits d’auteur" : "Intellectual property"
                        }</Link>
                        <Link to="/terms" className="link-editorial hover:text-[color:var(--navy)]">{
                            lang === "es" ? "Términos" : lang === "nl" ? "Voorwaarden" : lang === "fr" ? "Conditions" : "Terms"
                        }</Link>
                        <button
                            type="button"
                            data-testid="footer-cookie-settings"
                            onClick={() => window.__liap?.openConsent?.()}
                            className="link-editorial hover:text-[color:var(--navy)]"
                        >{
                            lang === "es" ? "Preferencias de cookies" : lang === "nl" ? "Cookievoorkeuren" : lang === "fr" ? "Préférences de cookies" : "Cookie settings"
                        }</button>
                    </nav>
                </div>
            </div>
        </footer>
    );
}
